import React, { useRef } from "react";

import { Calculator, Rocket } from "lucide-react";
import CTALarge from "../CTA/CTALarge.jsx";
import Seo from "../Seo.jsx";
import CTAButtonLink from "../CTA/CTAButtonLink.jsx";
import ScrollModal from "../ui/ScrollModal.jsx";

import Tooltip from "./Tooltip";
import "./guides.css";
import guidesConfig from "../../config/guidesConfig.js";
import fbc from './images/calculator-fb.gif';
import { OddsMonkeyFT } from "../Affiliate/Om.jsx";
import fbho from './images/fbhigherodds.png'

const FreeBetValueGuide = () => {
  const meta = guidesConfig.freeBetValueGuide?.seo || {};
  const scrollRef = useRef(null);

  return (
    <div className="guides-content">
      <Seo
        title={meta.title}
        description={meta.description}
        url={guidesConfig.freeBetValueGuide?.route || "/guides"}
      />

      <p>
        If you're new to matched betting, one of the first things you’ll notice is that a <strong>£10 free bet</strong> never gives you £10 profit. In fact, when done correctly, most matched bettors only make around <strong>£7 to £8 profit</strong> from a £10 free bet.
      </p>
      <p>
        In this guide, we’ll explain why free bets are worth less than face value, how it fits into your matched betting strategy, and how to <strong>maximise your profit</strong> from every free bet you get.
      </p>

      <h2>What Is a Free Bet Offer?</h2>
        <p>
        A <strong>free bet</strong> is a common bookmaker promotion. You might get one for signing up to a new bookie or as a reward for placing a{" "}
        <Tooltip content="A bet that meets the terms to qualify for a promotion">
            qualifying bet
        </Tooltip>.
        </p>
        <p>
        The key thing to remember is this: unlike a normal bet, if your free bet wins, you only get the <strong>winnings</strong> — not the <strong>stake</strong>. The stake belongs to the bookie and isn’t returned.
        </p>
        <p>
        For example, a £10 free bet at odds of 3.0 returns <strong>£20 profit</strong>. A normal £10 bet would return £30 (£20 profit + £10 stake).
        </p>
        <p>
        Matched bettors use free bets to lock in <strong>guaranteed profit</strong> by laying the same outcome on a <Tooltip content="A site where you can bet against an outcome against other punters, often used in matched betting">betting exchange</Tooltip>.
        </p>


      <CTAButtonLink 
        to="/get-started/step1"
        text="Understand How Matched Betting Works in 180 Seconds"
        buttonText="Start now"
        icon={Rocket}
        tabText="Popular"
      />

      <h2>Why the Stake Matters</h2>
      <p>Let’s look at a quick example.</p>

      <h3>Normal Bet (with your own money)</h3>
      <ul>
        <li>Stake: £10</li>
        <li>Odds: 5.0</li>
        <li>Returns: £10 (stake) + £40 (profit) = <strong>£50 total return</strong></li>
      </ul>

      <h3>Free Bet</h3>
      <ul>
        <li>Stake: £10 (bookie’s money)</li>
        <li>Odds: 5.0</li>
        <li>Returns: £0 (stake not returned) + £40 (profit) = <strong>£40 total return</strong></li>
      </ul>

      <p>
        So even if both bets win, the <strong>free bet returns £10 less</strong> — because the stake isn’t included.
      </p>
      <p>
        That means right from the start, your free bet isn’t worth £10. It’s worth £10 minus the missing stake — which is why it can never return the full amount as profit.
      </p>
      <h2>This is Why We Use Free Bets on Higher Odds</h2>

        <p>
        When using a <strong>free bet</strong>, you don’t get the <strong>stake</strong> back — only the <strong>winnings</strong>. So the higher the odds, the bigger the winnings — and the more actual cash you can turn into profit when matched betting.
        </p>
        <p>
        For example, placing a £10 free bet at odds of 1.2 would only return £2 profit. But the same £10 free bet at odds of 5.0 gives you £40 profit before laying.
        </p>
        <h6 className="info">Free bets should always be used at odds over 5.0, where possible.</h6>
        <p>
  Odds of <strong>5.0</strong> or over give you a solid return (£40 profit from a £10 free bet), without making your{" "}
  <Tooltip content="The amount of money you risk losing when you lay a bet at the exchange. If your lay bet loses, you have to cover that amount.">lay liability</Tooltip> too high. 
  A high lay liability ties up your bankroll when the exchange freezes enough to cover potential losses, and makes it harder to manage multiple bets at once.
</p>
<img src={fbho} alt="Why use free bets at higher odds" />

<p>
  Around odds of 5.0-7.0, you get the best of both: a strong return and a manageable lay liability. That’s why matched bettors often stick to odds between <strong>5.0 and 7.0</strong> when using free bets, particularly when starting out.
</p>

<h6 className="info">
          Use <a href="https://www.oddsmonkey.com/affliates/affiliate.php?id=66317" target="_blank" rel="noreferrer">OddsMonkey</a> to find the best matches available to maximise profit from your free bet, keep track of bets and profits, and find free bet offers. OddsMonkey helps you find matches that enable you to lock in more like 75-85% of a free bet.
        </h6>

      <h2>Locking in Profit With Matched Betting</h2>
      <p>
        As matched bettors, we use <Tooltip content="A platform where you can bet against an outcome happening">betting exchanges</Tooltip> like <span className="key">Smarkets</span> or <span className="key">Betfair</span> to <Tooltip content="Placing a bet against a certain outcome">lay</Tooltip> the same outcome they back with their free bet. This guarantees a profit no matter what happens.
      </p>
      <p>But laying the free bet isn’t perfect. Here’s why:</p>
      <ul>
        <li>
          <strong>Lay odds</strong> are usually slightly higher than the back odds.
        </li>
        <li>
          The exchange might charge a <Tooltip content="A small fee (usually 2–5%) on your winnings">commission</Tooltip> on your winnings.
        </li>
      </ul>

      <div className="example-container">


      <h4>Example: £10 Free Bet at Odds of 7.0</h4>
      <ScrollModal scrollContainerRef={scrollRef} props={{ctaKey: "om2"}} />

      <h6 className="info">Step 1: Place Free Bet</h6>
      <p>You back a team at odds of <strong>7.0</strong> using your £10 free bet.</p>
      <p>If the team wins, you get <strong>£40 profit</strong> (stake not returned).</p>
    <img src={fbc} alt="Free bet matched betting calculator" />
      <h6 className="info">Step 2: Place Lay Bet</h6>
      <p>
        You lay the same team on the exchange at odds of 7.5. The commission is <strong>2%</strong>.
      </p>
      <p>
        After running the numbers through a matched betting calculator, you see you'll lock in a minimum profit of <strong>£7.86</strong> — no matter the result.
      </p>
    <p>You'll get to keep 78.6% off  the free bet's face value.</p>
      </div>


      <CTALarge 
        to="/calculators/free-bet-calculator" 
        text="Go to Free Bet Calculator" 
        icon={Calculator} 
      />

      <h2>So Why Do You Only Keep 70–80%?</h2>
      <p>Let’s break it down:</p>

      <table>
        <thead>
          <tr>
            <th>Reason</th>
            <th>Impact</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Stake isn’t returned</td>
            <td>Loses you 10-30% of the face value</td>
          </tr>
          <tr>
            <td>Lay odds aren’t perfect</td>
            <td>Small loss on the lay side</td>
          </tr>
          <tr>
            <td>Exchange commission</td>
            <td>Slight reduction in profit (2-5%)</td>
          </tr>
        </tbody>
      </table>

      <p>
        Put together, these factors reduce your free bet’s value to around <strong>70–80%</strong> of its face value.
      </p>
      

      <h2>Tips to Maximise Free Bet Value</h2>
      <ul>
        <li>
          <strong>Use high odds</strong> (ideally 5.0–7.0) to increase the value of your back bet.
        </li>
        <li>
          <strong>Look for close matches</strong> between back and lay odds.
        </li>
        <li>
          Use <a href="https://www.oddsmonkey.com/affiliates/affiliate.php?id=66317" target="_blank" rel="noreferrer">OddsMonkey</a> to find the best matches available to maximise profit from your free bet, keep track of bets and profits, and find free bet offers.
        </li>
      </ul>
<OddsMonkeyFT/>
      <h2>Final Thoughts</h2>
      <p>
        Free bets are one of the biggest ways to make money from matched betting. But they’re not “free money” in the way most people think. Because the <strong>stake isn’t returned</strong>, and due to small <strong>lay losses</strong> and <strong>commission</strong>, you’ll usually end up with <strong>70–80% of the free bet value</strong>.
      </p>
      <p>
        That’s perfectly normal — and still a great deal. A £10 free bet giving you £7.50 profit with no risk? You’ll take that all day.
      </p>
    </div>
  );
};

export default FreeBetValueGuide;
